import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';

function PearlX() {
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const { pathname } = useLocation();
    const sectionRef = useRef<HTMLElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container' >
            <div>
                <Breadcrumb route="/case-studies" pageTitle="PearlX" />
            </div>
            <section className='article-row-1' id='pearlx-background'>
                <p>Energy</p>
                <h2>PearlX</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: Charlottesville, Virginia 🇺🇲</span>
                    </div>
                </div>
            </section>


            <section className='article-intro-section'>
                <h1>How We Built PearlX's State Of The Art Customer Portal</h1>
                <p>
                    PearlX is an innovative energy startup transforming how multi-housing facilities access sustainable power. Through their groundbreaking model, they provide affordable clean energy solutions that benefit both property owners and residents. Property owners receive monthly lease payments while residents enjoy discounted on-site energy, creating a sustainable future for residential communities with improved dependability and reduced costs.
                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>PearlX struggled to build their customer portal—a key element to their business—due to the limited resources they could access as a startup. If they hadn't been able to partner up with a service provider to quickly build a high-quality, user-friendly platform, they would have faced a delayed go-to-market timeline and cost inefficiencies.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We built a robust customer portal for our client. As we worked towards delivering an intuitive platform, we architected data infrastructure, built seamless onboarding processes, and implemented user profiles with custom permissions.</p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>PearlX now benefits from a streamlined onboarding process, efficient community management, and a scalable, user-friendly portal that will help them attract and retain new clients. We remain their trusted development partner to this day.</p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>PearlX aims to democratize sustainable energy for renters and landlords through innovative virtual power plants.</h3>
                <p>
                    PearlX's primary struggle was their inability to build a customer portal due to limited resources as a startup. They needed a partner with the necessary competencies to architect and build a platform where landlords could manage their communities and renters could opt into their service. Providing a seamless experience for all clients was essential.
                </p>
                <p>
                    A business like PearlX requires an easy-to-use platform for landlords and renters to manage energy consumption, monitor savings, and engage with the services offered. In a rapidly growing market, having a state-of-the-art customer portal is critical to maintaining a competitive edge and attracting new clients.
                </p>
                <p>
                    The potential challenges they faced included:
                </p>
                <ul>
                    <li>Cost-ineffectiveness in creating a customer portal</li>
                    <li>Slowed-down go-to-market timeline</li>
                    <li>The inability to take on best-in-class architects and builders</li>
                </ul>
            </section>

            <section className='article-section-3'>

                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    We worked with PearlX to analyze and outline the current vision and the roadmap for the architecture and UX of their customer portal. The architecture and the front-end are powered by HTML, JavaScript, CSS, and Python codebase, utilizing the React.js framework and a number of AWS services such as AWS Kinesis, AWS Lambda, and PostgreSQL.
                </p>
                <p>
                    We then built the logic that powers PearlX's document management for onboarding and created various user profiles with associated permissions and logic to handle community management and renter onboarding.
                </p>
                <p>
                    The final step consisted of architecting and building a data infrastructure to manage and house PearlX's customer portal with data security and backup protocols for key data. This architecture was then wrapped behind the customer portal with a contemporary UX/UI design.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>Analyzed and outlined the current vision and the roadmap for their customer portal.</li>
                    <li>Created document management logic.</li>
                    <li>Developed different permissions and logic for user profiles to enable PearlX's nuanced onboarding process.</li>
                    <li>Architected data infrastructure for onboarding and document management.</li>
                    <li>Designed UX/UI and conducted front-end development.</li>
                </ul>

                <h3>The Results</h3>
                <ul>
                    <li>PearlX can now seamlessly onboard renters and landlords.</li>
                    <li>We created community-specific onboarding powering the nuanced onboarding process and document management.</li>
                    <li>PearlX can finally enable its landlords to manage communities effectively.</li>
                </ul>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    The business logic we created enables an intuitive, user-centric approach to onboarding and community management. Our logic powers the nuanced, community-specific onboarding process and document management, allowing landlords and admins to effectively manage their various community members.
                </p>

                <h3>Future Plans</h3>
                <p>
                    Amalgam continues to be PearlX's sole development partner for all of their technological needs, ensuring ongoing support and innovation as they grow.
                </p>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>


            <section className='ourwork-row-14 blog-row-14'>

                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>
            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}

export default PearlX;